/**
 * Marketplace competitor comparison cluster (SEO).
 *
 * Facts about third-party platforms are described in general terms from their
 * own public pages and carry a review date. Fees, tiers and policies change
 * often, so nothing here is presented as a permanent claim.
 */

export type MpCompareRow = { dimension: string; behot: string; them: string };

export type MpComparison = {
  /** URL segment under /compare/marketplace/ */
  slug: string;
  brand: string;
  h1: string;
  title: string;
  description: string;
  keywords: string[];
  source?: string;
  reviewed?: string;
  intro: string;
  summary: string[];
  rows: MpCompareRow[];
  faq: [string, string][];
  related: string[];
};

const REVIEWED = "27 August 2026";

const BASE_ROWS = (them: {
  listingCost: string;
  commission: string;
  visibility: string;
  payout: string;
  boards: string;
  audience: string;
}): MpCompareRow[] => [
  {
    dimension: "Cost to publish a service",
    behot: "Free. Publishing and editing a service costs nothing.",
    them: them.listingCost,
  },
  {
    dimension: "Platform commission",
    behot: "15% on completed orders, deducted before the Earn wallet is credited.",
    them: them.commission,
  },
  {
    dimension: "Paid visibility",
    behot: "Featuring for 10 Heat (5 days) or 25 Heat (30 days), plus optional HOT board promotion.",
    them: them.visibility,
  },
  {
    dimension: "Ranked public boards",
    behot: "Yes. Services can enter public HOT boards where ranking is the Heat behind the listing and fans can boost it.",
    them: them.boards,
  },
  {
    dimension: "Payment method",
    behot: "Wallet only. Orders are paid with Heat; top-ups are invoiced in AED with sequential PDF invoices.",
    them: "Card and local payment methods at checkout according to its own public pages.",
  },
  {
    dimension: "Seller verification",
    behot: "Mandatory KYC for individuals and KYB with trade licence for companies before the first service goes live.",
    them: "Identity checks apply in the cases described on its own public pages.",
  },
  {
    dimension: "Payouts",
    behot: "Earn wallet, 48h verification, 5 to 8 working days processing, minimum US$100, bank (EUR/USD/AED) or USDT/USDC ERC-20.",
    them: them.payout,
  },
  {
    dimension: "Audience model",
    behot: "Marketplace sits inside an attention platform: public boards, profiles, referrals and promotion feed the same seller page.",
    them: them.audience,
  },
  {
    dimension: "Referral credit",
    behot: "2 Heat per signup and 10 Heat for every US$100 a referred user spends, usable on boosts and orders.",
    them: "Check the current public affiliate or referral terms.",
  },
  {
    dimension: "Languages",
    behot: "Eight languages across the whole platform, including guides and comparison pages.",
    them: "Check the current public experience per locale.",
  },
];

export const MP_COMPARISONS: MpComparison[] = [
  {
    slug: "fiverr",
    brand: "Fiverr",
    h1: "BeHot.Now Marketplace vs Fiverr",
    title: "BeHot.Now vs Fiverr — Creator Marketplace Comparison",
    description:
      "How the BeHot.Now Marketplace compares with Fiverr: free publishing, 15% platform fee, wallet checkout, mandatory verification and public Heat-ranked boards.",
    keywords: ["fiverr alternative", "behot vs fiverr", "creator marketplace comparison", "sell services alternative"],
    source: "https://www.fiverr.com/",
    reviewed: REVIEWED,
    intro:
      "Fiverr is a large general gig marketplace. BeHot.Now is an attention platform with a marketplace inside it, so selling sits next to public rankings, profiles and promotion rather than being the whole product.",
    summary: [
      "If you only need volume of buyers, a large horizontal marketplace has more of it. If you are building an audience — as a creator, artist, studio or brand — BeHot.Now lets the same profile rank publicly, sell services and earn referral credit from one wallet.",
      "The commercial difference is where you pay: BeHot.Now takes nothing to publish and 15% on completed orders, and sells optional visibility as fixed-price featuring rather than ongoing bidding.",
    ],
    rows: BASE_ROWS({
      listingCost: "Publishing gigs is free on the standard plan according to its own public pages.",
      commission: "A seller commission plus buyer-side fees apply; check the current published rates.",
      visibility: "Paid promotion and ad products are offered to eligible sellers.",
      boards: "No public pay-to-rank board where third parties can boost a seller listing.",
      payout: "Payout providers and clearing periods described on its own public pages.",
      audience: "Marketplace search and internal recommendation are the main discovery surfaces.",
    }),
    faq: [
      ["Is BeHot.Now cheaper than Fiverr?", "Publishing is free and the platform fee is a flat 15% on completed orders. Compare that with the current published seller and buyer fees on the other side before deciding."],
      ["Can I use both?", "Yes. Many sellers keep a horizontal marketplace for volume and use BeHot.Now for audience building and higher-intent buyers."],
      ["Do I need Heat to sell?", "Only for professional activation and optional visibility. Orders themselves cost you nothing up front."],
      ["Is the comparison up to date?", `Third-party details were checked on ${REVIEWED} against public pages and change often.`],
    ],
    related: ["upwork", "contra", "soundbetter"],
  },
  {
    slug: "upwork",
    brand: "Upwork",
    h1: "BeHot.Now Marketplace vs Upwork",
    title: "BeHot.Now vs Upwork — Fees, Bidding and Visibility Compared",
    description:
      "BeHot.Now Marketplace compared with Upwork: no connects or bidding, free publishing, 15% platform fee, wallet checkout and public Heat-ranked service boards.",
    keywords: ["upwork alternative", "behot vs upwork", "freelance platform comparison", "no connects freelancing"],
    source: "https://www.upwork.com/",
    reviewed: REVIEWED,
    intro:
      "Upwork is a contract-work platform built around proposals and hourly contracts. BeHot.Now sells packaged services with fixed scope, and replaces proposal credits with optional fixed-price visibility.",
    summary: [
      "There is no per-proposal credit system on BeHot.Now. You publish packages for free and, if you want placement, you buy a fixed featuring window instead of spending credits to apply.",
      "Buyers who prefer to describe a need can post a request and receive offers, but the default direction is buyer finds package, buyer orders.",
    ],
    rows: BASE_ROWS({
      listingCost: "Creating a profile is free; applying to jobs consumes purchasable connects.",
      commission: "A service fee applies to freelancer earnings; check the current published rate.",
      visibility: "Boosted proposals and availability badges are sold as paid options.",
      boards: "No public pay-to-rank board where third parties can boost a seller listing.",
      payout: "Multiple withdrawal methods with their own fees and clearing periods.",
      audience: "Job posts and search; discovery is employer-driven rather than audience-driven.",
    }),
    faq: [
      ["Do I have to bid for work?", "No. You publish packages. Buyers can also post requests and receive offers, but there is no per-application credit."],
      ["Are there hourly contracts?", "The marketplace is built around fixed-scope packages, which keeps disputes about scope rather than time."],
      ["What is the commission?", "15% of completed orders on BeHot.Now."],
      ["Is the comparison up to date?", `Third-party details were checked on ${REVIEWED} against public pages and change often.`],
    ],
    related: ["fiverr", "peopleperhour", "contra"],
  },
  {
    slug: "contra",
    brand: "Contra",
    h1: "BeHot.Now Marketplace vs Contra",
    title: "BeHot.Now vs Contra — Commission-Free Claims Compared",
    description:
      "BeHot.Now Marketplace compared with Contra: how a 15% platform fee with free publishing compares to commission-free positioning, and what each gives you for discovery.",
    keywords: ["contra alternative", "behot vs contra", "commission free freelancing", "independent creator platform"],
    source: "https://contra.com/",
    reviewed: REVIEWED,
    intro:
      "Contra positions itself around independent professionals and commission-free work. BeHot.Now charges a flat platform fee on completed orders and puts the money it earns into a public ranking and promotion layer.",
    summary: [
      "A commission-free model shifts monetisation to subscriptions and add-ons. BeHot.Now has no subscription at all: publishing is free, the fee applies only when you actually get paid.",
      "The bigger structural difference is discovery. BeHot.Now sellers can enter public boards where supporters boost them, which is not a feature of a portfolio-first network.",
    ],
    rows: BASE_ROWS({
      listingCost: "Publishing a profile and services is free according to its own public pages.",
      commission: "Positions itself as commission-free for freelancers; check the current published terms and paid tiers.",
      visibility: "Discovery is profile and network driven, with paid tiers where offered.",
      boards: "No public pay-to-rank board where third parties can boost a seller listing.",
      payout: "Payout options and timing described on its own public pages.",
      audience: "Portfolio and network first, with independent-professional positioning.",
    }),
    faq: [
      ["Why pay 15% at all?", "It funds moderation, verification, disputes, payouts and the ranking layer, and it only applies when you are paid."],
      ["Is there a subscription?", "No. BeHot.Now has no subscription anywhere."],
      ["What do I get that a portfolio network does not offer?", "Public Heat-ranked boards, fan boosts, referral credit and promotion products in the same account."],
      ["Is the comparison up to date?", `Third-party details were checked on ${REVIEWED} against public pages and change often.`],
    ],
    related: ["fiverr", "upwork", "peopleperhour"],
  },
  {
    slug: "soundbetter",
    brand: "SoundBetter",
    h1: "BeHot.Now Marketplace vs SoundBetter",
    title: "BeHot.Now vs SoundBetter — Music Services Marketplace Compared",
    description:
      "BeHot.Now Marketplace compared with SoundBetter for music services: mixing, mastering and production packages, fees, verification and public music boards.",
    keywords: ["soundbetter alternative", "sell mixing and mastering", "music services marketplace", "hire a producer"],
    source: "https://soundbetter.com/",
    reviewed: REVIEWED,
    intro:
      "SoundBetter is a specialist marketplace for music professionals. BeHot.Now covers the same services but connects them to artist pages, music boards and promotion campaigns in one account.",
    summary: [
      "For a mixing engineer or producer, the practical difference is what happens around the order: on BeHot.Now the same profile ranks on the music board, sells packages and earns referral credit.",
      "Music is a first-class branch of the marketplace taxonomy, with subcategories for mixing, mastering, production, vocals, DJ mixes, artwork and video.",
    ],
    rows: BASE_ROWS({
      listingCost: "Profiles and listings are free at the base tier according to its own public pages.",
      commission: "A platform commission applies to completed projects; check the current published rate.",
      visibility: "Paid membership tiers and placement options where offered.",
      boards: "No public pay-to-rank board where fans can boost a professional's listing.",
      payout: "Payout methods and clearing described on its own public pages.",
      audience: "Music-industry buyers searching for specific credits and roles.",
    }),
    faq: [
      ["Is BeHot.Now music-only?", "No, but music is a first-class category with deep subcategories and its own live boards."],
      ["Can fans pay for my studio time?", "Fans can boost your listings and buy your packages; earnings land in the Earn wallet."],
      ["Do I need credits or a membership?", "No membership. Professional activation is a one-off 99 Heat."],
      ["Is the comparison up to date?", `Third-party details were checked on ${REVIEWED} against public pages and change often.`],
    ],
    related: ["airgigs", "fiverr", "kwork"],
  },
  {
    slug: "airgigs",
    brand: "AirGigs",
    h1: "BeHot.Now Marketplace vs AirGigs",
    title: "BeHot.Now vs AirGigs — Session Musician Marketplace Compared",
    description:
      "BeHot.Now Marketplace compared with AirGigs for session musicians and audio services: packaged scope, fees, wallet checkout, verification and public boards.",
    keywords: ["airgigs alternative", "session musician marketplace", "hire session players", "audio services marketplace"],
    source: "https://www.airgigs.com/",
    reviewed: REVIEWED,
    intro:
      "AirGigs focuses on session musicians and audio services. BeHot.Now offers the same packaged model and adds public ranking, promotion and referral mechanics around it.",
    summary: [
      "Both platforms sell defined packages rather than hourly contracts, which keeps scope clear. The difference is what the platform does for your visibility after the listing exists.",
      "On BeHot.Now visibility is explicit and priced: featuring for a fixed window, or a board listing that anyone can boost.",
    ],
    rows: BASE_ROWS({
      listingCost: "Listing services is free according to its own public pages.",
      commission: "A commission applies to completed orders; check the current published rate.",
      visibility: "Internal search placement and featured slots where offered.",
      boards: "No public pay-to-rank board where fans can boost a professional's listing.",
      payout: "Payout methods and clearing described on its own public pages.",
      audience: "Artists and producers looking for specific instrumental and vocal work.",
    }),
    faq: [
      ["Can I offer session work here?", "Yes, under the music branch with subcategories for vocals, instruments, production and mixing."],
      ["How do buyers pay me?", "From their Heat wallet; your share lands in the Earn wallet after the 15% fee."],
      ["Can I keep listings on both platforms?", "Yes, nothing here is exclusive."],
      ["Is the comparison up to date?", `Third-party details were checked on ${REVIEWED} against public pages and change often.`],
    ],
    related: ["soundbetter", "fiverr", "kwork"],
  },
  {
    slug: "peopleperhour",
    brand: "PeoplePerHour",
    h1: "BeHot.Now Marketplace vs PeoplePerHour",
    title: "BeHot.Now vs PeoplePerHour — Fees and Discovery Compared",
    description:
      "BeHot.Now Marketplace compared with PeoplePerHour: free publishing, flat 15% platform fee, no proposal credits, wallet checkout and public Heat-ranked boards.",
    keywords: ["peopleperhour alternative", "behot vs peopleperhour", "freelance marketplace comparison", "hourlie alternative"],
    source: "https://www.peopleperhour.com/",
    reviewed: REVIEWED,
    intro:
      "PeoplePerHour mixes proposals with fixed offers. BeHot.Now is fixed-offer only, with optional paid placement instead of proposal credits and tiered commissions.",
    summary: [
      "A flat 15% is easier to plan against than a tiered commission that changes with lifetime billings per client. Publishing costs nothing either way here.",
      "Requests exist for buyers who prefer to describe a need, but sellers are never charged to respond.",
    ],
    rows: BASE_ROWS({
      listingCost: "Publishing fixed offers is free; sending proposals consumes monthly credits.",
      commission: "A tiered service fee applies based on billings per client; check the current published rates.",
      visibility: "Paid promotion and featured placement where offered.",
      boards: "No public pay-to-rank board where third parties can boost a seller listing.",
      payout: "Withdrawal methods and fees described on its own public pages.",
      audience: "Buyer briefs and internal search.",
    }),
    faq: [
      ["Does responding to a request cost me anything?", "No. Sellers are never charged to send an offer on BeHot.Now."],
      ["Is the fee tiered?", "No. It is a flat 15% on completed orders."],
      ["What replaces proposal credits?", "Optional fixed-price featuring and board promotion, bought only when you want them."],
      ["Is the comparison up to date?", `Third-party details were checked on ${REVIEWED} against public pages and change often.`],
    ],
    related: ["upwork", "fiverr", "kwork"],
  },
  {
    slug: "kwork",
    brand: "Kwork",
    h1: "BeHot.Now Marketplace vs Kwork",
    title: "BeHot.Now vs Kwork — Fixed-Price Service Marketplaces Compared",
    description:
      "BeHot.Now Marketplace compared with Kwork: fixed-price packages, commission, paid placement, verification requirements and public Heat-ranked service boards.",
    keywords: ["kwork alternative", "fixed price service marketplace", "behot vs kwork", "sell packages online"],
    source: "https://kwork.com/",
    reviewed: REVIEWED,
    intro:
      "Kwork sells standardised fixed-price services. BeHot.Now shares that packaged model but layers public ranking, fan boosts and a promotion suite on top.",
    summary: [
      "Both platforms remove hourly negotiation. The comparison therefore comes down to fees, verification and how a new seller gets their first views.",
      "On BeHot.Now, a new seller can buy a defined visibility window or enter a board rather than waiting on internal ranking alone.",
    ],
    rows: BASE_ROWS({
      listingCost: "Publishing services is free according to its own public pages.",
      commission: "A platform commission applies to completed orders; check the current published rate.",
      visibility: "Paid placement and promotion options inside the catalogue.",
      boards: "No public pay-to-rank board where third parties can boost a seller listing.",
      payout: "Withdrawal methods and minimums described on its own public pages.",
      audience: "Catalogue search inside the marketplace.",
    }),
    faq: [
      ["How does a new seller get first views here?", "Free catalogue placement, plus optional featuring or a HOT board listing."],
      ["Is verification mandatory?", "Yes, KYC or KYB before the first service goes live."],
      ["What currencies are used?", "Heat inside the platform; top-ups are invoiced in AED and payouts are made in EUR, USD, AED or stablecoin."],
      ["Is the comparison up to date?", `Third-party details were checked on ${REVIEWED} against public pages and change often.`],
    ],
    related: ["fiverr", "peopleperhour", "freelancer"],
  },
  {
    slug: "freelancer",
    brand: "Freelancer.com",
    h1: "BeHot.Now Marketplace vs Freelancer.com",
    title: "BeHot.Now vs Freelancer.com — Bidding vs Packaged Services",
    description:
      "BeHot.Now Marketplace compared with Freelancer.com: packaged services instead of bidding wars, flat 15% fee, free publishing and public Heat-ranked boards.",
    keywords: ["freelancer.com alternative", "bidding vs packages", "behot vs freelancer", "freelance marketplace fees"],
    source: "https://www.freelancer.com/",
    reviewed: REVIEWED,
    intro:
      "Freelancer.com is built around competitive bidding on posted projects. BeHot.Now inverts that: sellers publish priced packages, and buyers order what they see.",
    summary: [
      "Bidding pushes prices down and consumes time before any money moves. Packaged selling puts the price in public and turns competition into positioning instead of undercutting.",
      "Where a bid credit buys a chance to be read, Heat on BeHot.Now buys a defined placement or a board position that fans can reinforce.",
    ],
    rows: BASE_ROWS({
      listingCost: "Free membership tiers exist; bids beyond the monthly allowance are limited or paid.",
      commission: "Project and milestone fees apply; check the current published rates.",
      visibility: "Paid bid upgrades and membership tiers.",
      boards: "No public pay-to-rank board where third parties can boost a seller listing.",
      payout: "Withdrawal methods and fees described on its own public pages.",
      audience: "Project posts with competitive bidding.",
    }),
    faq: [
      ["Can buyers still post a brief?", "Yes, requests exist, and sellers can respond for free."],
      ["Are there membership tiers?", "No. There is no subscription on BeHot.Now."],
      ["What stops a race to the bottom?", "Public packages, ranked boards and verified sellers reward positioning rather than undercutting."],
      ["Is the comparison up to date?", `Third-party details were checked on ${REVIEWED} against public pages and change often.`],
    ],
    related: ["upwork", "kwork", "fiverr"],
  },
];

export const MP_COMPARISON_SLUGS = MP_COMPARISONS.map((c) => c.slug);

export function getMpComparison(slug: string): MpComparison | undefined {
  return MP_COMPARISONS.find((c) => c.slug === slug);
}
